import { useEffect, useRef, useState } from "react";
import { pitchLabel } from "@/lib/pitchLabel";
import api from "@/lib/api";
import { Calendar } from "@/components/ui/calendar";
import { format, parseISO, startOfWeek, addDays } from "date-fns";
import { Printer, ChevronLeft, ChevronRight, ImageDown, Share2, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import * as htmlToImage from "html-to-image";
import { FABadge, isFAImported } from "@/components/app/FABadge";

const DAY_LABELS = ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"];

export default function FixturePoster() {
  const nav = useNavigate();
  const posterRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const canShare = typeof navigator !== "undefined" && !!navigator.share && !!navigator.canShare;

  const renderPng = async () => {
    const dataUrl = await htmlToImage.toPng(posterRef.current, { pixelRatio: 2, cacheBust: true, backgroundColor: "#FFFFFF" });
    const blob = await (await fetch(dataUrl)).blob();
    return { dataUrl, blob, name: `EYFC-fixtures-${format(weekStart, "yyyy-MM-dd")}.png` };
  };
  const downloadImage = async () => {
    if (!posterRef.current) return;
    setBusy(true);
    try {
      const { dataUrl, name } = await renderPng();
      const a = document.createElement("a"); a.download = name; a.href = dataUrl; a.click();
      toast.success("Image downloaded — ready to post");
    } catch { toast.error("Couldn't render the poster image"); }
    finally { setBusy(false); }
  };
  const shareImage = async () => {
    if (!posterRef.current) return;
    setBusy(true);
    try {
      const { blob, name } = await renderPng();
      const file = new File([blob], name, { type: "image/png" });
      if (navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "Elsenham YFC fixtures", text: `Fixtures w/c ${format(weekStart, "d MMM")}` });
      } else { throw new Error("unsupported"); }
    } catch (e) { if (e?.name !== "AbortError") { toast.info("Sharing isn't available here — downloading instead"); await downloadImage(); } }
    finally { setBusy(false); }
  };
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/fixtures/week", { params: { start_date: format(weekStart, "yyyy-MM-dd") } });
      setData(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [weekStart]);

  const shiftWeek = (days) => setWeekStart(prev => addDays(prev, days));

  return (
    <div>
      {/* Screen-only controls */}
      <div className="print:hidden mb-6 flex items-center justify-between flex-wrap gap-4">
        <div>
          <button onClick={() => nav(-1)} className="text-sm opacity-60 hover:opacity-100 mb-3">← Back to Admin</button>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#83B68B]">Print-ready</div>
          <h1 className="font-display font-black text-4xl md:text-5xl mt-2 leading-tight">Weekly fixture poster</h1>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={()=>shiftWeek(-7)} className="p-2 rounded border border-border hover:border-[#83B68B]" data-testid="poster-prev-week"><ChevronLeft className="w-4 h-4" /></button>
          <button onClick={()=>setWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }))} className="text-xs uppercase tracking-widest px-3 py-2 rounded border border-border hover:border-[#83B68B]">This week</button>
          <button onClick={()=>shiftWeek(7)} className="p-2 rounded border border-border hover:border-[#83B68B]" data-testid="poster-next-week"><ChevronRight className="w-4 h-4" /></button>
          <button onClick={downloadImage} disabled={busy} data-testid="poster-download-image" className="text-[11px] tracking-widest uppercase px-4 py-2 rounded bg-[#83B68B] text-white hover:bg-[#6ba173] inline-flex items-center gap-1.5 disabled:opacity-50 ml-2">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageDown className="w-4 h-4" />} Download as image
          </button>
          {canShare && (
            <button onClick={shareImage} disabled={busy} data-testid="poster-share" className="text-[11px] tracking-widest uppercase px-4 py-2 rounded border border-[#83B68B]/60 text-[#83B68B] hover:bg-[#83B68B]/10 inline-flex items-center gap-1.5 disabled:opacity-50">
              <Share2 className="w-4 h-4" /> Share
            </button>
          )}
          <button onClick={()=>window.print()} data-testid="poster-print-btn" className="btn-volt px-5 py-2 rounded-md inline-flex items-center gap-2">
            <Printer className="w-4 h-4" /> Print / Save PDF
          </button>
        </div>
      </div>

      {/* Poster — visible in both modes; enforce printable colours */}
      <div id="poster-sheet" ref={posterRef} className="poster-sheet bg-white text-[#0F172A] rounded-md shadow-2xl mx-auto" style={{ maxWidth: "820px" }}>
        <div className="p-10 print:p-8">
          {/* Header band */}
          <div className="flex items-center justify-between border-b-4 border-[#0F172A] pb-6">
            <div className="flex items-center gap-4">
              <img src="/logo.jpg" alt="Elsenham YFC" className="w-16 h-16 object-contain" />
              <div>
                <div className="font-display font-black text-3xl leading-none">ELSENHAM YOUTH FC</div>
                <div className="text-xs tracking-[0.3em] uppercase text-[#0F172A]/60 mt-1">Weekly fixtures</div>
              </div>
            </div>
            <div className="text-right">
              <div className="font-display font-black text-2xl">{format(weekStart, "d MMM")} — {format(addDays(weekStart, 6), "d MMM yyyy")}</div>
              <div className="text-xs tracking-widest uppercase text-[#0F172A]/60 mt-1">Week commencing {format(weekStart, "EEEE d MMMM")}</div>
            </div>
          </div>

          {loading || !data ? (
            <div className="py-16 text-center opacity-60">Loading…</div>
          ) : (
            <div className="mt-6 space-y-4" data-testid="poster-days">
              {data.days.every(d => !d.bookings.some(b => b.session_type === "game")) && (
                <div className="py-10 text-center text-[#0F172A]/50 italic" data-testid="poster-no-games">No games this week</div>
              )}
              {data.days.map((day, i) => ({ ...day, i, bookings: day.bookings.filter(b => b.session_type === "game") })).filter(day => day.bookings.length > 0).map((day) => (
                <div key={day.date} className="grid grid-cols-12 gap-4 border-b border-[#0F172A]/10 pb-4 last:border-0" data-testid={`poster-day-${day.date}`}>
                  <div className="col-span-3">
                    <div className="font-display font-black text-xl">{DAY_LABELS[day.i]}</div>
                    <div className="text-sm text-[#0F172A]/60">{format(parseISO(day.date), "d MMM")}</div>
                  </div>
                  <div className="col-span-9">
                      <div className="space-y-2">
                        {day.bookings.map(b => (
                          <div key={b.id} className="flex items-baseline gap-3 text-sm">
                            <span className="font-display font-black text-base w-14 flex-shrink-0">{b.start_hour.toString().padStart(2,"0")}:00</span>
                            <span className="text-[10px] tracking-widest uppercase px-1.5 py-0.5 rounded flex-shrink-0 bg-[#0F172A] text-[#83B68B]">
                              {b.match_type || "game"}
                            </span>
                            {isFAImported(b) && <FABadge variant="light" />}
                            <span className="font-medium">{pitchLabel(b)}</span>
                            {b.team_name && <span className="text-[#0F172A]/60">· {b.team_name}</span>}
                            {b.opponent && <span className="text-[#0F172A]/80">vs <strong>{b.opponent}</strong></span>}
                            {b.referee_name && <span className="text-[#0F172A]/50 text-xs">(Ref: {b.referee_name})</span>}
                          </div>
                        ))}
                      </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Footer band */}
          <div className="mt-8 pt-4 border-t-4 border-[#0F172A] flex items-center justify-between text-xs uppercase tracking-widest text-[#0F172A]/60">
            <div>Est · Train · Play</div>
            <div>{data ? `${data.total} fixtures` : ""}</div>
            <div>elsenhamyfc.booking</div>
          </div>
        </div>
      </div>

      <style>{`
        @media print {
          @page { size: A4; margin: 12mm; }
          body { background: white !important; }
          .poster-sheet { box-shadow: none !important; max-width: 100% !important; }
          nav, header, .grain::before { display: none !important; }
        }
      `}</style>
    </div>
  );
}
